import React from 'react';
import { useIntl } from 'react-intl';
import { TRPCClientErrorLike } from '@trpc/client';
import { DefaultErrorShape } from '@trpc/server/unstable-core-do-not-import';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faChartBar } from '@fortawesome/pro-light-svg-icons/faChartBar';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TypedMemo } from '../../../common/types';
import { useChartTokens } from '../../theme/useChartTokens';
import { ChartSkeleton } from '../ChartSkeleton/ChartSkeleton';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';

export interface ListeningScorePoint {
  date: string;
  score: number;
}

interface ListeningScoreChartProps {
  title: string;
  data: ListeningScorePoint[];
  isLoading?: boolean;
  error?: TRPCClientErrorLike<{ transformer: true; errorShape: DefaultErrorShape }> | null;
  emptyLabel: string;
  errorLabel: string;
}

const ListeningScoreChartComponent: React.FC<ListeningScoreChartProps> = ({
  title,
  data,
  isLoading,
  error,
  emptyLabel,
  errorLabel,
}) => {
  const intl = useIntl();
  const chart = useChartTokens();

  // Format the ISO date into a locale-aware short label for the axis + tooltip.
  const chartData = React.useMemo(
    () =>
      data.map((point) => ({
        ...point,
        label: new Date(point.date).toLocaleDateString(intl.locale, { month: 'short', day: 'numeric' }),
      })),
    [data, intl.locale]
  );

  return (
    <Panel className="h-100">
      <PanelHead title={title} />
      <PanelBody>
        {isLoading === true ? (
          <div style={{ height: '260px' }}>
            <ChartSkeleton />
          </div>
        ) : error ? (
          <div className="d-flex align-items-center justify-content-center text-center text-muted py-5">
            <div>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mb-2 d-block mx-auto"
                style={{ fontSize: '2rem', color: chart.neg }}
              />
              {errorLabel}
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center text-center text-muted py-5">
            <div>
              <FontAwesomeIcon icon={faChartBar} className="mb-2 d-block mx-auto" style={{ fontSize: '2rem' }} />
              {emptyLabel}
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: chart.axis }} />
              <YAxis tick={{ fontSize: 11, fill: chart.axis }} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: chart.tooltipBg, color: chart.tooltipTx }} />
              <Line type="monotone" dataKey="score" stroke={chart.brand} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </PanelBody>
    </Panel>
  );
};

export const ListeningScoreChart = TypedMemo(ListeningScoreChartComponent);
